import { cva } from 'class-variance-authority';

export type RatingStarScoreSize = 'compact' | 'default' | 'medium' | 'large';
export type RatingStarTheme = 'light' | 'dark';

export const RATING_STAR_TONES = ['default', 'theme'] as const;
export type RatingStarTone = (typeof RATING_STAR_TONES)[number];

export interface RatingStarVariantProps {
  scoreSize?: RatingStarScoreSize;
  theme?: RatingStarTheme;
}

export const ratingStarStrokeVariants = cva('', {
  variants: {
    starTone: {
      default: 'text-warning-500',
      theme: 'text-rating-chrome',
    },
  },
  defaultVariants: { starTone: 'default' },
});

export const ratingStarFillVariants = cva('', {
  variants: {
    starTone: {
      default: 'fill-warning-500 text-warning-500',
      theme: 'fill-rating-star text-rating-star',
    },
  },
  defaultVariants: { starTone: 'default' },
});

const iconVariants = cva('shrink-0', {
  variants: {
    scoreSize: {
      compact: 'h-3 w-3',
      default: 'h-4 w-4',
      medium: 'h-5 w-5',
      large: 'h-6 w-6',
    },
  },
  defaultVariants: { scoreSize: 'default' },
});

// Button's base styles size nested svgs via a descendant rule, which outranks
// a class on the icon itself — the override has to sit on the trigger.
const triggerIconVariants = cva('', {
  variants: {
    scoreSize: {
      compact: '[&_svg]:size-3',
      default: '[&_svg]:size-4',
      medium: '[&_svg]:size-5',
      large: '[&_svg]:size-6',
    },
  },
  defaultVariants: { scoreSize: 'default' },
});

const copyVariants = cva('tabular-nums', {
  variants: {
    theme: {
      light: 'text-neutral-700',
      dark: 'text-white',
    },
  },
  defaultVariants: { theme: 'light' },
});

const textVariant = {
  compact: 'l1',
  default: 'b4',
  medium: 'b2',
  large: 'b1',
} as const;

export function ratingStarVariants({
  scoreSize = 'default',
  theme = 'light',
}: RatingStarVariantProps = {}) {
  return {
    icon: iconVariants({ scoreSize }),
    triggerIcon: triggerIconVariants({ scoreSize }),
    copy: copyVariants({ theme }),
    text: textVariant[scoreSize],
  };
}
